import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { SearchableSelect, type SearchableOption } from '@/components/form/SearchableSelect';
import { Textarea } from '@/components/form/Textarea';
import { useToast } from '@/components/ui/useToast';
import { listPackings, listBoxes } from '@/api/packing';
import { createOutwardGrn, getLastTransport } from '@/api/outwardGrn';
import { apiError } from '@/lib/apiError';
import { useFieldErrors } from '@/lib/useFieldErrors';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function OutwardGrnModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const fe = useFieldErrors<'packingId' | 'vehicleNo' | 'lrNo' | 'transporterName'>();
  const [packingId, setPackingId] = useState<number | null>(null);
  const [transporterName, setTransporter] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [driverName, setDriver] = useState('');
  const [lrNo, setLrNo] = useState('');
  const [gateOfficer, setGateOfficer] = useState('');
  const [isPartial, setIsPartial] = useState(false);
  const [partialNote, setPartialNote] = useState('');
  const [holdReason, setHoldReason] = useState('');
  const verifiedQuery = useQuery({
    queryKey: ['packing', 'verified-for-grn'],
    queryFn: () => listPackings({ status: 'Verified' }),
    enabled: open,
  });
  const verifiedPackings = useMemo(() => verifiedQuery.data ?? [], [verifiedQuery.data]);

  // Online packings are known by their Shopify order number; others by pack + DO.
  const packingOptions = useMemo<SearchableOption[]>(
    () => verifiedPackings.map((p) => ({
      value: p.id,
      label: p.onlineOrderName ?? `${p.packNo} · ${p.pickList?.dispatchOrder?.doNo ?? '—'}`,
      sublabel: `${p.onlineCustomer ?? p.pickList?.dispatchOrder?.party?.name ?? '—'} · ${p.boxCount} box(es)`,
    })),
    [verifiedPackings],
  );
  const selected = useMemo(
    () => verifiedPackings.find((p) => p.id === packingId) ?? null,
    [verifiedPackings, packingId],
  );

  // Per-box dimensions + weights for the chosen packing.
  const boxesQuery = useQuery({
    queryKey: ['packing-boxes', packingId],
    queryFn: () => listBoxes(packingId as number),
    enabled: open && packingId != null,
  });
  const boxes = useMemo(() => boxesQuery.data ?? [], [boxesQuery.data]);
  // Pre-fill Transporter / Vehicle / Driver from this customer's last dispatch.
  // Applied once per selected packing, so a refetch never overwrites edits.
  const lastTransportQuery = useQuery({
    queryKey: ['outward-grn-last-transport', packingId],
    queryFn: () => getLastTransport(packingId as number),
    enabled: open && packingId != null,
  });
  const lastTransport = lastTransportQuery.data ?? null;
  const prefilledFor = useRef<number | null>(null);
  useEffect(() => {
    if (packingId == null || !lastTransportQuery.isSuccess || prefilledFor.current === packingId) return;
    prefilledFor.current = packingId;
    if (!lastTransport) return;
    setTransporter(lastTransport.transporterName ?? '');
    setVehicleNo(lastTransport.vehicleNo ?? '');
    setDriver(lastTransport.driverName ?? '');
    fe.clear('transporterName');
    fe.clear('vehicleNo');
  }, [packingId, lastTransportQuery.isSuccess, lastTransport, fe]);

  const fmtDim = (v: number | null) => (v == null ? '—' : v.toLocaleString('en-IN', { maximumFractionDigits: 1 }));

  useEffect(() => {
    if (open) {
      prefilledFor.current = null;
      setPackingId(null);
      setTransporter('');
      setVehicleNo('');
      setDriver('');
      setLrNo('');
      setGateOfficer('');
      setIsPartial(false);
      setPartialNote('');
      setHoldReason('');
    }
  }, [open]);

  const createMut = useMutation({
    mutationFn: createOutwardGrn,
    onSuccess: async (r) => {
      qc.invalidateQueries({ queryKey: ['outward-grn'] });
      qc.invalidateQueries({ queryKey: ['packing'] });
      if (r.status === 'Weight-Hold') {
        toast.push(
          `${r.grnNo} HELD — weight variance ${Number(r.variancePct).toFixed(1)}% exceeds tolerance. Sent to Director / Admin for override.`,
          'warning',
        );
      } else {
        toast.pushStep(`Outward GRN ${r.grnNo} raised`, {
          body: 'Next step — issue the GST invoice.',
          href: '/sales/sp-invoice',
          nextLabel: 'Open Invoices',
          tag: `outward-${r.id}`,
        });
      }
      onClose();
    },
    onError: (e) => {
      toast.push(apiError(e, 'Failed'), 'error');
    },
  });

  function submit() {
    if (!fe.validate({
      packingId: !packingId && 'Select a verified packing first',
      transporterName: !transporterName.trim() && 'Transporter is required',
      vehicleNo: !vehicleNo.trim() && 'Vehicle # is required',
    })) return;
    createMut.mutate({
      packingId: packingId!,
      transporterName: transporterName || undefined,
      vehicleNo,
      driverName: driverName || undefined,
      lrNo: lrNo || undefined,
      gateOfficer: gateOfficer || undefined,
      isPartial,
      partialNote: isPartial ? partialNote : undefined,
      holdReason: holdReason.trim() || undefined,
    });
  }

  return (
    <Modal
      open={open}
      title={selected?.onlineOrderName ? `Raise Outward GRN — ${selected.onlineOrderName}` : 'Raise Outward GRN'}
      onClose={onClose}
      size="xl"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={createMut.isPending || !packingId}
          >
            {createMut.isPending ? 'Issuing…' : 'Issue Outward GRN'}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-2 sm:gap-3">
        <FormGroup label="Verified Packing" required error={fe.errors.packingId}>
          {/* Searchable list that stays inside the window (a native select's options
              stretch to the longest entry and spill off a phone screen). */}
          <SearchableSelect
            options={packingOptions}
            value={packingId ?? ''}
            onChange={(v) => {
              setPackingId(v === '' ? null : Number(v));
              fe.clear('packingId');
            }}
            placeholder="Select a verified packing…"
            keepOrder
            className={fe.has('packingId') ? 'rounded-lg ring-1 ring-danger' : undefined}
          />
        </FormGroup>

        {selected && (
          <div className="rounded-md border border-[var(--border-soft)] bg-[var(--bg-soft)] p-2 text-xs sm:p-3">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <div className="text-[10px] uppercase text-[var(--text-muted)]">{selected.onlineOrderName ? 'Order ID' : 'DO Ref'}</div>
                <div className="font-semibold">
                  {selected.onlineOrderName ?? selected.pickList?.dispatchOrder?.doNo ?? '—'}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-[var(--text-muted)]">Customer</div>
                <div className="font-semibold">
                  {selected.onlineCustomer ?? selected.pickList?.dispatchOrder?.party?.name ?? '—'}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-[var(--text-muted)]">
                  Boxes / Weight
                </div>
                <div className="font-semibold">
                  {selected.boxCount} · {selected.totalWeightKg} kg
                </div>
              </div>
            </div>
            {(selected.pickList?.dispatchOrder?.enquiry?.remarks || selected.pickList?.dispatchOrder?.remarks) && (
              <div className="mt-2 space-y-1 border-t border-[var(--border-soft)] pt-2">
                {selected.pickList?.dispatchOrder?.enquiry?.remarks && (
                  <div>
                    <span className="text-[10px] font-bold uppercase text-[var(--text-muted)]">Enquiry Note</span>{' '}
                    <span className="text-[var(--text-secondary)]">{selected.pickList.dispatchOrder.enquiry.remarks}</span>
                  </div>
                )}
                {selected.pickList?.dispatchOrder?.remarks && (
                  <div>
                    <span className="text-[10px] font-bold uppercase text-[var(--text-muted)]">DO Remark</span>{' '}
                    <span className="text-[var(--text-secondary)]">{selected.pickList.dispatchOrder.remarks}</span>
                  </div>
                )}
              </div>
            )}
            {boxes.length > 0 && (
              <div className="mt-2 border-t border-[var(--border-soft)] pt-2">
                <div className="mb-1 text-[10px] font-bold uppercase text-[var(--text-muted)]">Boxes — Dimensions &amp; Weight</div>
                <div className="space-y-0.5">
                  {boxes.map((b) => (
                    <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 text-[11px]">
                      <span className="font-semibold">Box {b.boxNo}</span>
                      <span className="text-[var(--text-secondary)]">
                        {(b.lengthCm != null || b.widthCm != null || b.heightCm != null)
                          ? `${fmtDim(b.lengthCm)}×${fmtDim(b.widthCm)}×${fmtDim(b.heightCm)} cm`
                          : 'no dimensions'}
                        {' · '}Gross {b.grossWeightKg ?? '—'} kg · Net {b.netWeightKg ?? '—'} kg
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {lastTransport && prefilledFor.current === packingId && (
          <p className="mb-1.5 text-[11px] text-[var(--text-muted)]">
            Transporter, vehicle and driver pre-filled from this customer's last dispatch ({lastTransport.grnNo}, {new Date(lastTransport.grnDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}) — edit if different.
          </p>
        )}
        <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 sm:gap-3">
          <FormGroup label="Transporter *" error={fe.errors.transporterName}>
            <Input
              invalid={fe.has('transporterName')}
              value={transporterName}
              onChange={(e) => { setTransporter(e.target.value); fe.clear('transporterName'); }}
            />
          </FormGroup>
          <FormGroup label="Vehicle # *" error={fe.errors.vehicleNo}>
            <Input invalid={fe.has('vehicleNo')} value={vehicleNo} onChange={(e) => { setVehicleNo(e.target.value); fe.clear('vehicleNo'); }} />
          </FormGroup>
          <FormGroup label="Gross Weight (kg)">
            <Input
              value={selected ? `${selected.totalWeightKg} kg` : ''}
              readOnly
              placeholder="auto from packing"
              className="opacity-80"
            />
          </FormGroup>
          <FormGroup label="Driver Name">
            <Input value={driverName} onChange={(e) => setDriver(e.target.value)} />
          </FormGroup>
          <FormGroup label="LR / Docket #" error={fe.errors.lrNo}>
            <Input invalid={fe.has('lrNo')} value={lrNo} onChange={(e) => { setLrNo(e.target.value); fe.clear('lrNo'); }} />
          </FormGroup>
          <FormGroup label="Gate Officer">
            <Input value={gateOfficer} onChange={(e) => setGateOfficer(e.target.value)} />
          </FormGroup>
        </div>

        <div className="rounded-md border border-[var(--border-color)] bg-[var(--bg-soft)] p-2 text-[10px] leading-snug text-[var(--text-muted)] sm:p-2.5 sm:text-[11px]">
          A dispatch weight check runs automatically from the packing weights (Σ box gross − tare)
          against the expected net from the item master. Outside tolerance holds the GRN for
          Director / Admin override. (Skipped if the packing has no recorded weights.)
        </div>

        <FormGroup label="Reason (if the packed weight may be out of tolerance)">
          <Textarea
            rows={2}
            value={holdReason}
            onChange={(e) => setHoldReason(e.target.value)}
            placeholder="e.g. carton heavier due to extra protective padding"
          />
        </FormGroup>

        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={isPartial}
            onChange={(e) => setIsPartial(e.target.checked)}
          />
          Mark as Partial Dispatch
        </label>
        {isPartial && (
          <FormGroup label="Partial Dispatch Note">
            <Textarea
              rows={2}
              value={partialNote}
              onChange={(e) => setPartialNote(e.target.value)}
            />
          </FormGroup>
        )}
      </div>
    </Modal>
  );
}
